import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Receipt, TestTube, Scan, RefreshCw, Banknote, CheckCircle2, FileText,
} from 'lucide-react';
import { toast } from 'react-toastify';
import createAuthedAxios from '../../hooks/useAxiosAuth';
import baseURL from '../../utils/baseURL';
import CollectPaymentModal from '../../components/billing/CollectPaymentModal';
import useHospitalCurrency from '../../hooks/useHospitalCurrency';
import { formatPrice } from '../../utils/currency';
import { hospitalPatientCode } from '../../utils/patientCode';

// SERVICE BILLING (Phase 4, #16) — the billing counter's ONE place for lab and
// scan bills: fulfilled orders arrive here, "Generate bill" raises the tax
// invoice on the shared invoices table, and payment is collected against it.
// Pharmacy keeps its own counter (a dispense is stock); consult fees keep the
// appointment payment flow.

const TABS = [
  { id: '', labelKey: 'billing.all', fallback: 'All' },
  { id: 'lab', labelKey: 'billing.lab', fallback: 'Lab', Icon: TestTube },
  { id: 'radiology', labelKey: 'billing.scans', fallback: 'Scans', Icon: Scan },
];

// Every figure is in the hospital's currency, read through utils/currency.js
// (P24): orders and invoices name no currency of their own, and the page used
// to print the rupee sign for every hospital. Unknown -> the bare amount.
const money = (n, currency) => formatPrice(Number(n) || 0, currency, { decimals: 2 });

const ServiceBilling = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [type, setType] = useState('');
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyOrderId, setBusyOrderId] = useState(null);
  const [payInvoice, setPayInvoice] = useState(null);
  const currency = useHospitalCurrency();

  const load = useCallback(async (which = type) => {
    setLoading(true);
    try {
      const axiosInstance = createAuthedAxios();
      const qs = which ? `?type=${which}` : '';
      const { data } = await axiosInstance.get(`${baseURL}/billing/orders${qs}`);
      setOrders(data.orders || []);
    } catch (err) {
      toast.error(err?.response?.data?.message || t('billing.load_failed', 'Could not load the billing worklist'));
    } finally {
      setLoading(false);
    }
  }, [type, t]);

  useEffect(() => { load(type); }, [load, type]);

  const generateBill = async (order) => {
    setBusyOrderId(order.orderId);
    try {
      const axiosInstance = createAuthedAxios();
      const { data } = await axiosInstance.post(`${baseURL}/billing/orders/${order.orderId}/invoice`, {});
      toast.success(t('billing.billed', { no: data.invoice?.invoiceNo, defaultValue: `Bill ${data.invoice?.invoiceNo} raised` }));
      await load();
    } catch (err) {
      const code = err?.response?.data?.code;
      if (code === 'ALREADY_BILLED') {
        toast.info(t('billing.already_billed', 'This order is already billed'));
        await load();
      } else {
        toast.error(err?.response?.data?.message || t('billing.bill_failed', 'Could not raise the bill'));
      }
    } finally {
      setBusyOrderId(null);
    }
  };

  return (
    <div data-testid="service-billing-page">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-5">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#4ABDBD] to-[#2D8A8A] flex items-center justify-center shadow-md">
            <Receipt size={20} className="text-white" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-gray-800">{t('billing.service_title', 'Service billing')}</h1>
            <p className="text-xs text-gray-400">{t('billing.service_sub', 'Bill completed lab and scan orders, collect payments')}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <div className="bg-gray-100 rounded-xl p-1 flex">
            {TABS.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setType(tab.id)}
                className={`px-3.5 py-1.5 rounded-lg text-sm font-medium transition-colors cursor-pointer flex items-center gap-1.5 ${type === tab.id ? 'bg-white shadow-sm text-[#2D8A8A]' : 'text-gray-500 hover:text-gray-700'}`}
                data-testid={`billing-tab-${tab.id || 'all'}`}
              >
                {tab.Icon && <tab.Icon size={14} />}
                {t(tab.labelKey, tab.fallback)}
              </button>
            ))}
          </div>
          <button onClick={() => load()} className="p-2 rounded-lg hover:bg-gray-100 text-gray-500 transition-colors cursor-pointer" title={t('common.refresh', 'Refresh')}>
            <RefreshCw size={17} />
          </button>
        </div>
      </div>

      <div className="bg-white rounded-2xl shadow-md overflow-hidden">
        {loading ? (
          <div className="p-6 space-y-3">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="animate-pulse flex items-center gap-4">
                <div className="w-9 h-9 rounded-lg bg-gray-100" />
                <div className="flex-1 space-y-2">
                  <div className="h-3 bg-gray-100 rounded w-1/3" />
                  <div className="h-2.5 bg-gray-100 rounded w-1/4" />
                </div>
                <div className="h-8 w-28 bg-gray-100 rounded-lg" />
              </div>
            ))}
          </div>
        ) : orders.length === 0 ? (
          <div className="p-12 text-center">
            <FileText size={40} className="mx-auto text-gray-200 mb-3" />
            <p className="text-sm text-gray-500">{t('billing.nothing', 'Nothing waiting to be billed.')}</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs text-gray-400 border-b border-gray-100">
                  <th className="px-5 py-3 font-medium">{t('billing.order', 'Order')}</th>
                  <th className="px-5 py-3 font-medium">{t('billing.patient', 'Patient')}</th>
                  <th className="px-5 py-3 font-medium">{t('billing.items', 'Items')}</th>
                  <th className="px-5 py-3 font-medium text-right">{t('billing.amount', 'Amount')}</th>
                  <th className="px-5 py-3 font-medium text-right">{t('billing.action', 'Action')}</th>
                </tr>
              </thead>
              <tbody>
                {orders.map((o) => (
                  <tr key={o.orderId} className="border-b border-gray-50 hover:bg-gray-50/60 transition-colors">
                    <td className="px-5 py-3.5">
                      <div className="flex items-center gap-2.5">
                        <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${o.orderType === 'lab' ? 'bg-violet-50 text-violet-600' : 'bg-sky-50 text-sky-600'}`}>
                          {o.orderType === 'lab' ? <TestTube size={15} /> : <Scan size={15} />}
                        </div>
                        <div>
                          {/* The order's kind, not its orderId (owner, 29 Sep 2026:
                              order ids are hidden). */}
                          <p className="font-medium text-gray-800">{o.orderType === 'lab' ? t('billing.lab', 'Lab') : t('billing.scans', 'Scans')}</p>
                          <p className="text-[11px] text-gray-400">{o.orderedAt ? new Date(o.orderedAt).toLocaleDateString() : ''}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-5 py-3.5">
                      <button
                        onClick={() => navigate(`/billing/patient/${o.patientId}`)}
                        className="text-gray-700 hover:text-[#2D8A8A] transition-colors cursor-pointer"
                        title={t('billing.view_patient_bills', "This patient's bills")}
                      >
                        {o.patientName || t('common.patient_label', 'Patient')}
                      </button>
                      {hospitalPatientCode(o) && (
                        <p className="text-[11px] text-gray-400">{hospitalPatientCode(o)}</p>
                      )}
                    </td>
                    <td className="px-5 py-3.5 text-gray-500 max-w-[260px] truncate">
                      {o.items.map((it) => it.name).join(', ')}
                    </td>
                    <td className="px-5 py-3.5 text-right font-semibold text-gray-800">
                      {money(o.invoice ? o.invoice.grandTotal : o.estimate, currency)}
                    </td>
                    <td className="px-5 py-3.5 text-right">
                      {o.invoice ? (
                        Number(o.invoice.balance) > 0 && o.invoice.status !== 'cancelled' ? (
                          <button
                            onClick={() => setPayInvoice(o.invoice)}
                            className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-lg bg-amber-50 text-amber-700 text-xs font-semibold hover:bg-amber-100 transition-colors cursor-pointer"
                            data-testid={`billing-collect-${o.orderId}`}
                          >
                            <Banknote size={13} /> {t('billing.collect', { amount: money(o.invoice.balance, currency), defaultValue: `Collect ${money(o.invoice.balance, currency)}` })}
                          </button>
                        ) : (
                          <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#2D8A8A]">
                            <CheckCircle2 size={14} /> {o.invoice.invoiceNo}
                          </span>
                        )
                      ) : (
                        <button
                          onClick={() => generateBill(o)}
                          disabled={busyOrderId === o.orderId || !o.items.length}
                          className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-lg bg-gradient-to-r from-[#4ABDBD] to-[#3AA8A8] text-white text-xs font-semibold shadow-sm hover:from-[#3AA8A8] hover:to-[#2D8A8A] transition-all disabled:opacity-50 cursor-pointer"
                          data-testid={`billing-generate-${o.orderId}`}
                        >
                          {busyOrderId === o.orderId
                            ? <span className="border-2 border-white border-t-transparent rounded-full w-3.5 h-3.5 animate-spin" />
                            : <Receipt size={13} />}
                          {t('billing.generate', 'Generate bill')}
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {payInvoice && (
        <CollectPaymentModal
          invoice={payInvoice}
          currency={currency}
          onClose={() => setPayInvoice(null)}
          onPaid={() => { setPayInvoice(null); load(); }}
        />
      )}
    </div>
  );
};

export default ServiceBilling;
